/**
 * Aperçu compact des résultats d'un sondage sur une carte bibliothèque.
 */

import { useEffect, useState } from 'react';
import type { PollResponse } from '../../types/library';
import type { CardNumber } from '../../types/game';
import { getCardName } from '../../utils/cards';
import { BACKEND_URL } from '../../config';

function voteLabel(r: PollResponse): string {
  const vt = r.vote_type ?? 'card';
  if (vt === 'bid') {
    const bid = r.card_played;
    return bid === 0 ? 'Passe' : bid === 1 ? 'Petite' : bid === 2 ? 'Garde' : bid === 3 ? 'G. sans' : bid === 4 ? 'G. contre' : String(bid);
  }
  if (vt === 'ecart') {
    if (r.vote_value) {
      return r.vote_value.split(',').map(Number).map(c => getCardName(c as CardNumber)).join(', ');
    }
    return `Écart (${r.card_played})`;
  }
  return getCardName(r.card_played as CardNumber);
}

interface PollResultsPreviewProps {
  pseudo: string;
  sharedGameId: number;
  voteCount: number;
  onOpenResults?: () => void;
}

export default function PollResultsPreview({
  pseudo,
  sharedGameId,
  voteCount,
  onOpenResults,
}: PollResultsPreviewProps) {
  const [responses, setResponses] = useState<PollResponse[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (voteCount <= 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/library/shared/${sharedGameId}/poll?pseudo=${encodeURIComponent(pseudo)}`,
        );
        if (res.ok && !cancelled) {
          const data = await res.json();
          setResponses(data.responses ?? []);
        }
      } catch { /* ignore */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [pseudo, sharedGameId, voteCount]);

  if (voteCount <= 0) return null;

  const votesByChoice = new Map<string, { count: number; label: string }>();
  for (const r of responses) {
    const vt = r.vote_type ?? 'card';
    const key = vt === 'ecart'
      ? `${vt}:${r.vote_value ?? r.card_played}`
      : `${vt}:${r.card_played}`;
    const existing = votesByChoice.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      votesByChoice.set(key, { count: 1, label: voteLabel(r) });
    }
  }

  const topChoices = Array.from(votesByChoice.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
  const total = responses.length || voteCount;

  return (
    <div
      className={`library-poll-preview${onOpenResults ? ' clickable' : ''}`}
      onClick={onOpenResults ? (e) => { e.stopPropagation(); onOpenResults(); } : undefined}
      role={onOpenResults ? 'button' : undefined}
      tabIndex={onOpenResults ? 0 : undefined}
      onKeyDown={onOpenResults ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpenResults();
        }
      } : undefined}
    >
      {!loaded && topChoices.length === 0 ? (
        <span className="library-poll-preview-loading">{voteCount} vote{voteCount !== 1 ? 's' : ''}…</span>
      ) : topChoices.length === 0 ? (
        <span className="library-poll-preview-loading">{voteCount} vote{voteCount !== 1 ? 's' : ''}</span>
      ) : (
        topChoices.map((choice, i) => (
          <div key={i} className="library-poll-preview-row">
            <span className="library-poll-preview-label">{choice.label}</span>
            <div className="library-poll-preview-bar-bg">
              <div
                className="library-poll-preview-bar"
                style={{ width: `${(choice.count / total) * 100}%` }}
              />
            </div>
            <span className="library-poll-preview-pct">
              {Math.round((choice.count / total) * 100)}%
            </span>
          </div>
        ))
      )}
      {onOpenResults && (
        <span className="library-poll-preview-more">Voir le détail</span>
      )}
    </div>
  );
}
